import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchChronicles, type Chapter } from './api';
import { highlightNames } from './format';
import { useChronicleTts } from './useChronicleTts';
// M5.14: The Chronicles — full-screen wall-monitor page
// shaped like a single page in a book.
//
// Layout: a single flowing column of prose. The newest
// paragraph is at the BOTTOM of the column (the eye
// naturally reads top-down; the latest hour is "what
// just happened" so it sits at the foot of the page
// where the reader's gaze lands after scanning the
// older context above). When a new chapter lands, the
// OLDEST visible paragraph slides up off the top of the
// page and the new one fades in at the bottom.
//
// No per-paragraph chrome: no title, no time marker.
// The api still persists chapter_title and hour_utc
// (the data is useful for analytics + future re-renders),
// but the page itself reads as continuous prose. A slim
// header (page title + UTC clock) and footer (back link)
// are the only chrome.
//
// Default collapsed: show the last DEFAULT_VISIBLE
// chapters so the wall TV at 1m+ still reads as a single
// page-turning. The "Show all" link expands to the full
// 24h window (server-side cap of EXPANDED_LIMIT
// chapters) so the operator can scroll back through the
// day. When new chapters arrive while expanded, prepend
// the newest to the bottom without changing the user's
// scroll position — the expand mode is a deliberate
// operator action; auto-animating under their feet is
// hostile UX.
//
// Polling: 30s. The cron lands a chapter at hh:55 UTC
// (apps/api/src/jobs/chronicles-cron.ts); 30s is well
// under that.
//
// Reduced-motion: slide-off + fade-in are disabled; the
// new paragraph just appears in place. Data still updates.
//
// Bypasses AppShell: dedicated monitor URL.

const DEFAULT_VISIBLE = 3;
const EXPANDED_LIMIT = 50;

export function ChroniclesPage() {
  // Fetch the full window up front so expanding the view
  // is instantaneous — no re-fetch needed. When collapsed
  // we show the latest DEFAULT_VISIBLE; when expanded we
  // show everything in the window. EXPANDED_LIMIT caps
  // the fetch (50 = ~2 days at the hourly cadence, well
  // past the 24h window the public-board route enforces).
  const { data } = useQuery({
    queryKey: ['chronicles'],
    queryFn: () => fetchChronicles(undefined, EXPANDED_LIMIT),
    refetchInterval: 30_000,
  });
  // Whether the operator has expanded the page to show
  // the full 24h. Lifted into state so the link /
  // collapse link + the rendering both read from the
  // same source. When the page first mounts it's
  // collapsed; the wall TV default.
  const [expanded, setExpanded] = useState(false);
  // Visible chapters in reading order: oldest at top,
  // newest at bottom. We track them as state so the
  // animation has a stable id to compare against on each
  // poll. The initial seed is the current data — the
  // page doesn't animate the very first render, only
  // subsequent arrivals.
  //
  // When collapsed, we slice to the last
  // DEFAULT_VISIBLE chapters (newest at the bottom).
  // When expanded, we render the full list.
  const [paragraphs, setParagraphs] = useState<Chapter[]>([]);
  const lastBottomIdRef = useRef<string | null>(null);
  const seedRef = useRef(false);
  // Mirror of `expanded` for the useEffect — lets the
  // effect detect expanded-toggled-without-new-chapter
  // transitions and recompute the visible list.
  const expandedRef = useRef(expanded);

  useEffect(() => {
    const incoming = data?.chapters ?? [];
    // Reverse so oldest is first (top), newest is last
    // (bottom). The API returns newest-first.
    const next = [...incoming].reverse();
    if (!seedRef.current) {
      // Seed the visible list. Cap at DEFAULT_VISIBLE
      // so a fresh load of a sparse window doesn't pile
      // 30 empty hours into the DOM.
      setParagraphs(next.slice(-DEFAULT_VISIBLE));
      seedRef.current = true;
      lastBottomIdRef.current = next[next.length - 1]?.id ?? null;
      return;
    }
    const incomingBottomId = next[next.length - 1]?.id ?? null;
    const expandedChanged = expandedRef.current !== expanded;
    if (expandedChanged) {
      // The operator toggled collapsed/expanded and no
      // new chapter has arrived. Recompute the visible
      // list to match the new mode — this is the path
      // that lets 'Show all' actually show all. Without
      // this branch, the effect's only update trigger
      // (a new chapter) never fires when the user only
      // clicks the toggle, and `paragraphs` stays
      // sliced to DEFAULT_VISIBLE.
      expandedRef.current = expanded;
      setParagraphs(expanded ? next : next.slice(-DEFAULT_VISIBLE));
      // Don't reset lastBottomIdRef — the new-chapter
      // branch below still works for new arrivals after
      // a toggle.
      return;
    }
    if (incomingBottomId && incomingBottomId !== lastBottomIdRef.current) {
      // New chapter at the bottom of the column.
      // Collapse mode: keep DEFAULT_VISIBLE; the oldest
      // will be removed from the DOM (CSS slide-off
      // animation handles the visual). Expand mode: append
      // the new one to whatever the operator is currently
      // viewing — don't auto-jump their scroll.
      const visible = expanded
        ? next
        : next.slice(-DEFAULT_VISIBLE);
      setParagraphs(visible);
      lastBottomIdRef.current = incomingBottomId;
    }
  }, [data?.chapters, expanded]);

  const reduceMotion = usePrefersReducedMotion();
  const tts = useChronicleTts();
  const now = useClock(1000);
  // M5.14 page age cap: the api returns the resolved
  // window so we can show the operator how fresh the
  // page is. Used in the header badge + the empty-state
  // copy ("showing chapters from the last 24h").
  const windowHours = data?.window_hours ?? 24;
  // The full list (newest-first from the api) — used
  // for the "Show all (N in last Xh)" link so the
  // operator can see how many chapters are hiding
  // behind the collapsed view. Cheap: it's the same
  // payload we already have.
  const allChapters = data?.chapters ?? [];

  return (
    // font-chronicle: opt into Uncial Antiqua for
    // every text node on this page (header, body,
    // footer, empty-state copy). The whole page is
    // a chapter; the rest of the app stays in sans.
    <div className="fixed inset-0 flex flex-col bg-bg text-ink font-chronicle">
      <header className="flex items-center justify-between border-b border-white/10 px-8 py-3">
        <div className="flex items-baseline gap-3">
          <span className="grid h-7 w-7 place-items-center rounded-md text-bg text-base font-bold" style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}>M</span>
          <span className="text-base font-semibold">The Chronicles</span>
          <span className="text-xs text-ink-dim" title="Next chapter lands at the next hh:55 UTC.">
            {now.toUTCString().slice(17, 25)} UTC
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/*
            M5.14 expand toggle. The operator clicked
            "Show all" — render every chapter in the
            window so they can scroll back through the
            day. Click "Collapse" to return to the last
            DEFAULT_VISIBLE (the wall-TV view). The link
            text reflects the current state.
          */}
          {!expanded ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs text-ink-dim underline-offset-4 hover:text-ink hover:underline"
            >
              Show all ({allChapters.length} in last {windowHours}h)
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-xs text-ink-dim underline-offset-4 hover:text-ink hover:underline"
            >
              Collapse to last {DEFAULT_VISIBLE}
            </button>
          )}
          <span className="text-xs text-ink-dim">
            {paragraphs.length} of last {DEFAULT_VISIBLE} · {windowHours}h window
          </span>
        </div>
      </header>

      <main
        className={expanded
          ? 'min-h-0 flex-1 overflow-y-auto px-12 py-10 md:px-20 md:py-14'
          : 'min-h-0 flex-1 overflow-hidden px-12 py-10 md:px-20 md:py-14'}
      >
        {paragraphs.length === 0 ? (
          <div className="grid h-full place-items-center text-ink-dim">
            <div className="text-center">
              <p style={{ fontSize: 'clamp(0.95rem, 1.4vw, 1.3rem)' }}>
                The chronicle has not yet begun.
              </p>
              {/*
                M5.14 page age cap: when the page is empty
                because chapters have expired (not because
                the cron is broken), show the operator the
                window so they understand why. The header
                already shows "24h window"; the empty-state
                copy is the operator's "is the cron dead?"
                check.
              */}
              <p className="mt-2 text-xs text-ink-dim">
                Showing chapters from the last {windowHours}h.
                {windowHours >= 24 ? ' A quiet weekend will expire Friday-evening prose before Sunday morning.' : ' Tight window — chapters expire quickly.'}
              </p>
            </div>
          </div>
        ) : expanded ? (
          // Expanded mode: render every chapter in the
          // window as normal <p> blocks. No typewriter
          // (these aren't the new arrivals — they're
          // historical); no slide-off animation; no
          // justify-end (the user is reading top-down).
          // The "newest is at the bottom" principle is
          // preserved: chronological order, oldest at top.
          //
          // Scroll: the parent <main> has min-h-0 +
          // overflow-y-auto set on the className above.
          // That combo is the load-bearing rule — flex
          // children default to min-height: auto (their
          // content's intrinsic height) which lets them
          // grow past the available height without
          // triggering the parent's overflow. min-h-0
          // forces the main to be bounded at its
          // allocated flex-1 height, and overflow-y-auto
          // then actually scrolls. Without min-h-0, the
          // page grows past the viewport and the body's
          // own scrollbar is what the user sees (or they
          // see nothing, on the wall TV's overflow:hidden
          // body).
          <article
            className="flex w-full flex-col gap-8"
            style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.6rem)', lineHeight: 1.5 }}
          >
            {paragraphs.map((c) => (
              // v0.7.22: highlightNames wraps each actor in
              // <span class="chronicle-name"> which the
              // chronicle styles in a parchment-warm amber.
              // The render produces React nodes (no HTML
              // parsing, no dangerouslySetInnerHTML).
              <div key={c.id} className="flex items-start gap-3">
                <p className="min-w-0 flex-1">{highlightNames(c.paragraph, c.actor_names ?? [])}</p>
                <ReadAloudButton id={c.id} text={c.paragraph} tts={tts} />
              </div>
            ))}
            <p className="text-center text-xs text-ink-dim">
              — end of {windowHours}h window —
            </p>
          </article>
        ) : (
          <article
            className="flex h-full w-full flex-col justify-end gap-8"
            style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.6rem)', lineHeight: 1.5 }}
          >
            {paragraphs.map((c, i) => {
              // position === paragraphs.length - 1 is the
              // newest (bottom). It types out LTR on mount.
              const isNewest = i === paragraphs.length - 1;
              const animateIn = !reduceMotion && isNewest;
              const className = animateIn ? 'mirabili-chapter-in' : '';
              return animateIn ? (
                <Typewriter
                  key={c.id}
                  text={c.paragraph}
                  // v0.7.22: pass the highlighted version
                  // so the typewriter swaps to it once the
                  // typing completes. During typing the
                  // plain text reveals (cursor animates);
                  // once revealed, the name spans land in
                  // place. The expand/collapse effect of
                  // "Show all" toggles between this branch
                  // (collapsed typewriter) and the expanded
                  // plain render above, both of which
                  // highlight names.
                  revealed={highlightNames(c.paragraph, c.actor_names ?? [])}
                  className={className}
                  trailing={<ReadAloudButton id={c.id} text={c.paragraph} tts={tts} />}
                />
              ) : (
                <div key={c.id} className={`${className} flex items-start gap-3`}>
                  <p className="min-w-0 flex-1">
                    {highlightNames(c.paragraph, c.actor_names ?? [])}
                  </p>
                  <ReadAloudButton id={c.id} text={c.paragraph} tts={tts} />
                </div>
              );
            })}
          </article>
        )}
      </main>

      <footer className="flex items-center justify-end border-t border-white/10 px-8 py-2">
        <a
          href="/public-board"
          className="text-xs text-ink-muted underline-offset-4 hover:text-ink hover:underline"
        >
          ← back to public board
        </a>
      </footer>
    </div>
  );
}

// --- helpers ---

// Speaker button that reads a paragraph aloud via the server-side
// Kokoro TTS endpoint. Shows 🔊 (idle) / ⏳ (synthesizing) / ⏹
// (playing; click to stop).
function ReadAloudButton({
  id,
  text,
  tts,
}: {
  id: string;
  text: string;
  tts: ReturnType<typeof useChronicleTts>;
}) {
  const playing = tts.activeId === id;
  const icon = playing && tts.state === 'loading' ? '⏳' : playing ? '⏹' : '🔊';
  const label = playing ? 'Stop reading' : 'Read aloud';
  return (
    <button
      type="button"
      onClick={() => tts.toggle(id, text)}
      className="mt-1 shrink-0 rounded-full p-2 text-ink-muted transition-colors hover:bg-bg-elevated hover:text-ink"
      aria-label={label}
      title={label}
    >
      <span aria-hidden>{icon}</span>
    </button>
  );
}

// M5.14: typewriter reveal of the newest paragraph,
// one character at a time, ~60ms/letter. We split
// the paragraph into individual characters (preserving
// whitespace + punctuation) and reveal each one with a
// setTimeout-driven state slice. Using setTimeout instead
// of CSS keyframes here because we want the timer to
// match the exact text length (a 600-char paragraph at
// 60ms/char = 36s reveal, which feels right; a
// CSS-animation approach would either need a per-paragraph
// --duration variable or fixed-time keyframes that
// mis-align with the actual text). The component unmounts
// when the user navigates away (no cleanup race).
//
// Reduced-motion: the caller renders the full paragraph
// as a normal <p> instead of <Typewriter>, so this
// component never runs under reduced-motion.
//
// v0.7.22: optional `revealed` prop. When the typing
// completes, the typewriter swaps from the plain-text
// reveal to the `revealed` React node tree (typically
// the output of highlightNames(...)). This lets the
// page render the newest chapter with the typewriter
// animation (eye-catching on the wall TV) and the
// name-highlight once the text settles. During typing
// the plain text reveals; after typing the spans
// land in the right positions (the helper produces
// the same text with span-wrapped names). We don't
// try to render character-by-character through React
// nodes — that would require walking the node tree
// per character, which is wasteful and bug-prone.
function Typewriter({
  text,
  className,
  revealed,
  trailing,
}: {
  text: string;
  className?: string;
  revealed?: ReactNode;
  trailing?: ReactNode;
}) {
  const [shown, setShown] = useState(0);
  const total = text.length;
  useEffect(() => {
    if (shown >= total) return;
    // 30ms per character. A 600-char paragraph takes
    // ~18s — fast pace for a wall TV at 1m+. The eye
    // keeps up comfortably with the cursor; the page
    // feels alive without the prose dragging. Was
    // 60ms (v0.7.4) before the operator asked for
    // another boost.
    const t = setTimeout(() => setShown((n) => n + 1), 30);
    return () => clearTimeout(t);
  }, [shown, total]);
  return (
    <div className={`flex items-start gap-3 ${className ?? ''}`}>
      <p className="min-w-0 flex-1">
        {shown >= total && revealed !== undefined ? (
          // Typing complete: render the highlighted version.
          // The aria-label keeps the full text for screen readers;
          // the visible content is the React node tree produced
          // by highlightNames (which carries the same string
          // content with name spans wrapped).
          <span aria-label={text}>{revealed}</span>
        ) : (
          <>
            {/* The aria-label keeps the full text for screen
                readers; the visible text is the typed-prefix
                so the cursor / partial reveal doesn't read as
                garbage to AT. */}
            <span aria-label={text}>{text.slice(0, shown)}</span>
            {shown < total ? (
              <span aria-hidden="true" className="mirabili-cursor">|</span>
            ) : null}
          </>
        )}
      </p>
      {trailing}
    </div>
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

function useClock(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
